import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Input, Form, message, Modal } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { TbCirclesRelation } from "react-icons/tb";
import { otherApi } from '../api';

const RelationShips = () => {
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isConfirmDeleteVisible, setIsConfirmDeleteVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();

  // Form submission
  const handleOk = () => {
    form.validateFields()
      .then(values => {
        if (editingItem) {
          handleUpdate(values); // Call handleUpdate function
        } else {
          handleAdd(values); // Use handleAdd function to make API call
        }
        form.resetFields();
        setIsModalVisible(false);
        setEditingItem(null);
      })
      .catch(info => console.log('Validate Failed:', info));
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingItem(null);
  };

  const handleDeleteConfirm = () => {
    setIsConfirmDeleteVisible(false);
    handleDelete(); // Trigger delete process
  };

  const handleDeleteCancel = () => {
    setIsConfirmDeleteVisible(false);
    setEditingItem(null);
  };

  // Search functionality
  const handleSearch = e => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter(item =>
    (item.relationShip || '').toLowerCase().includes(searchText.toLowerCase())
  );

  // Action handlers
  const editItem = record => {
    setEditingItem(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const deleteItem = _id => {
    setEditingItem(data.find(item => item._id === _id));
    setIsConfirmDeleteVisible(true);
  };

  const fetchData = async () => {
    try {
      console.log('Fetching data...'); // Debug: log fetch initiation
      const response = await otherApi.get('/relationship');
      
      // Log the response to inspect its structure
      console.log('API Response:', response.data);
      
      // Ensure response.data is an array inside response.data.data
      if (Array.isArray(response.data.data)) {
        const dataWithKeys = response.data.data.map(item => ({
          key: item._id, // Use _id from response data as key
          ...item
        }));
        setData(dataWithKeys);
      } else {
        message.error('Unexpected response format.');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Failed to fetch relationships');
    }
  };

  // Add relationship
  const handleAdd = async (values) => {
    try {
      await otherApi.post('/relationship/addRelationShip', {
        relationShip: values.relationShip, // Adjust to match your form field name
      });
      message.success('Relationship added successfully!');
      fetchData(); // Refresh the data
    } catch (error) {
      console.error('Failed to add relationship:', error);
      message.error('Failed to add relationship. Please try again later.');
    }
  };

  // Update relationship
  const handleUpdate = async (values) => {
    try {
      if (!editingItem || !editingItem._id) {
        throw new Error('No ID provided for update');
      }
  
      const response = await otherApi.put(
        `/relationship/updateRelationShip/${editingItem._id}`, // Ensure this URL is correct
        { relationShip: values.relationShip }
      );
  
      if (response.data.success) {
        setData(prevData => prevData.map(item =>
          item._id === editingItem._id ? { ...item, ...values } : item
        ));
        message.success('Relationship updated successfully');
        fetchData(); // Refresh the data
      } else {
        throw new Error(response.data.message || 'Update failed');
      }
    } catch (error) {
      console.error('Failed to update relationship:', error);
      message.error('Failed to update relationship. Please try again later.');
    }
  };

  // Delete relationship
  const handleDelete = async () => {
    try {
      if (!editingItem || !editingItem._id) {
        throw new Error('No ID provided for deletion');
      }
  
      const response = await otherApi.delete(
        `/relationship/updateRelationShip/${editingItem._id}`
      );
  
      if (response.data.success) {
        setData(prevData => prevData.filter(item => item._id !== editingItem._id));
        message.success('Relationship deleted successfully');
        setEditingItem(null);
      } else {
        throw new Error(response.data.message || 'Deletion failed');
      }
    } catch (error) {
      console.error('Failed to delete relationship:', error);
      message.error('Failed to delete relationship. Please try again later.');
    }
  };

  useEffect(() => {
    fetchData(); // Fetch data when the component mounts
  }, []); // Empty dependency array ensures this runs only once

  // Define columns
  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      render: (text, record, index) => index + 1,
      width: '10%',
    },
    {
      title: 'Relationship',
      dataIndex: 'relationShip',
      key: 'relationShip',
      width: '20%',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} onClick={() => editItem(record)} />
          <Button icon={<DeleteOutlined />} onClick={() => deleteItem(record._id)} />
        </Space>
      ),
      width: '20%',
    },
  ];

  return (
    <div style={{ padding: '20px', boxSizing: 'border-box' }}>
      <h1 style={{ color: 'seagreen', textAlign: 'center', marginBottom: '20px' }}>
        <TbCirclesRelation /> Relationship
      </h1>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <Input
          placeholder="Search relationship"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={handleSearch}
          style={{ width: 200 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            setEditingItem(null);
            setIsModalVisible(true);
          }}
        >
          Add relationship
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={filteredData}
        pagination={{ pageSize: 10 }}
        rowKey="_id" // Use _id as the row key
      />
      <Modal
        title={editingItem ? "Edit Relationship" : "Add New Relationship"}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleOk}>
            {editingItem ? 'Save Changes' : 'Submit'}
          </Button>,
        ]}
      >
        <Form
          form={form}
          name="relationshipForm"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
        >
          <Form.Item
            label="Relationship"
            name="relationShip"
            rules={[{ required: true, message: 'Please input the Relationship!' }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Confirm Deletion"
        visible={isConfirmDeleteVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete"
        cancelText="Cancel"
      >
        <p>Are you sure you want to delete this item?</p>
      </Modal>
    </div>
  );
};

export default RelationShips;
